import { useState } from 'react';
import { customReportApi } from '../../../api/customReportApi';
import { apiError, downloadBaseName, languageName } from '../utils/customReportUtils';

/** Taking a finished version out of the app: JSON / PDF downloads and dig.watch newsletter drafts. */
export const useReportExports = ({
  scope, scopeId, selectedId, isOfficial, reportTitle, activeLanguage, translationLang, setError, onNotify,
}) => {
  const [downloading, setDownloading] = useState('');
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletters, setNewsletters] = useState({});

  const download = async (kind) => {
    if (isOfficial || !selectedId) return;
    const name = downloadBaseName(reportTitle, translationLang);
    setDownloading(kind);
    setError('');
    try {
      if (kind === 'json') {
        await customReportApi.downloadJson(scope, scopeId, selectedId, `${name}.json`, translationLang);
      } else {
        await customReportApi.downloadPdf(scope, scopeId, selectedId, `${name}.pdf`, translationLang);
      }
    } catch (err) {
      setError(apiError(err, `Failed to download ${kind.toUpperCase()}`));
    } finally {
      setDownloading('');
    }
  };

  /**
   * Send the language currently on screen to dig.watch as a newsletter draft.
   *
   * Only the JSON leaves here: banner, template, audience segment and the send
   * itself are decided by the editors on their side.
   */
  const createNewsletter = async () => {
    if (isOfficial || !selectedId) return;
    const langLabel = languageName(activeLanguage);
    if (!window.confirm(
      `Create a ${langLabel} newsletter draft on dig.watch from “${reportTitle || 'this report'}”?`
    )) return;

    setSendingNewsletter(true);
    setError('');
    try {
      const response = await customReportApi.createNewsletter(
        scope, scopeId, selectedId, activeLanguage
      );
      const created = response?.result || {};
      if (response?.newsletter) {
        setNewsletters((prev) => ({ ...prev, [response.lang]: response.newsletter }));
      }
      onNotify?.({
        type: 'success',
        title: created.duplicate ? 'Newsletter already there' : 'Newsletter draft created',
        message: created.duplicate
          ? `The ${langLabel} draft already existed on dig.watch, so nothing was duplicated.`
          : `The ${langLabel} draft is on dig.watch. The editors pick the audience and send it.`,
      });
    } catch (err) {
      setError(apiError(err, 'Failed to create the newsletter'));
    } finally {
      setSendingNewsletter(false);
    }
  };

  return { downloading, sendingNewsletter, newsletters, setNewsletters, download, createNewsletter };
};
